import React from 'react';
import { Box, Stack, Text, Tooltip as MantineTooltip } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { ChartTimelineProps } from '../types';
import { CombinedTimeline } from './CombinedTimeline';
import { useChartLayout, ChartMargins } from '../hooks';
import { formatChartDate } from '../utils';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import type { TooltipContentProps } from 'recharts';
import type {
  ValueType,
  NameType,
} from 'recharts/types/component/DefaultTooltipContent';

// Journal question item component that can use hooks
interface JournalQuestionItemProps {
  metric: any;
  topPosition: number;
  hasNumericData: boolean;
  isHovered: boolean;
  isLocked: boolean;
  isMobile: boolean;
  onLabelHover?: (metricId: string | null) => void;
  onLabelClick?: (metricId: string) => void;
}

const JournalQuestionItem: React.FC<JournalQuestionItemProps> = ({
  metric,
  topPosition,
  hasNumericData,
  isHovered,
  isLocked,
  isMobile,
  onLabelHover,
  onLabelClick,
}) => {
  const { t } = useTranslation();

  // Create a ref to measure the actual content height
  const [contentHeight, setContentHeight] = React.useState(24);
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (contentRef.current) {
      const height = contentRef.current.scrollHeight;
      setContentHeight(Math.max(24, height + 4)); // Minimum 24px + some padding
    }
  }, [metric.questionText, metric.name, isHovered, isLocked]);

  return (
    <Box
      style={{
        position: 'absolute',
        top: `${topPosition - 12}px`, // Offset to center with dot
        left: '0px',
        right: '8px',
        minHeight: '24px', // Minimum height for positioning
        cursor: hasNumericData ? 'pointer' : 'default',
      }}
      onMouseEnter={() => hasNumericData && onLabelHover?.(metric.id)}
      onMouseLeave={() => hasNumericData && onLabelHover?.(null)}
      onTouchStart={() => hasNumericData && onLabelHover?.(metric.id)}
      onTouchEnd={() =>
        hasNumericData && setTimeout(() => onLabelHover?.(null), 500)
      }
      onClick={() => hasNumericData && onLabelClick?.(metric.id)}
    >
      {/* Dynamic background box that adapts to content height */}
      <Box
        style={{
          position: 'absolute',
          top: '0px', // Expand upward when hovered or locked
          left: '0px',
          right: '0px',
          height: `${contentHeight - (isHovered || isLocked ? 4 : 0)}px`, // Dynamic height based on content
          borderRadius: '4px',
          backgroundColor: isLocked || isHovered ? '#e3f2fd' : 'transparent',
          border:
            isLocked || isHovered
              ? '1px solid #1976d2'
              : '1px solid transparent',
          transition: 'all 0.2s ease',
          zIndex: 1,
        }}
      />
      {/* Content container - flexible height */}
      <Box
        ref={contentRef}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minHeight: '24px',
          padding: '2px 4px',
          zIndex: 2,
        }}
      >
        <Box
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: metric.color,
            flexShrink: 0,
            boxShadow: hasNumericData ? '0 0 4px rgba(0,0,0,0.3)' : 'none',
          }}
        />
        <MantineTooltip
          label={
            hasNumericData
              ? `${isLocked ? t('graph.clickToUnlock') : t('graph.clickToLock')}`
              : metric.questionText || metric.name
          }
          openDelay={300}
          withArrow
          position="bottom"
          color="dark"
          multiline
          styles={{
            tooltip: {
              backgroundColor: '#1a1a1a',
              color: 'white',
              fontSize: '12px',
              padding: '8px 12px',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
              border: '1px solid #333',
              lineHeight: 1.4,
            },
            arrow: {
              borderColor: '#1a1a1a',
            },
          }}
        >
          <Text
            size={isMobile ? 'xs' : 'sm'}
            style={{
              lineHeight: 1.2,
              wordBreak: 'break-word',
              fontWeight: isLocked ? 500 : isHovered ? 500 : 400,
              color: isLocked || isHovered ? '#1976d2' : 'inherit',
            }}
          >
            {metric.questionText || metric.name}
            {hasNumericData && (
              <span style={{ marginLeft: '4px', opacity: 0.7 }}>📈</span>
            )}
          </Text>
        </MantineTooltip>
      </Box>
    </Box>
  );
};

// Custom dot component for journal metrics
interface CustomJournalDotProps {
  cx?: number;
  cy?: number;
  payload?: any;
  dataKey?: string;
  color: string;
  isMobile: boolean;
}

const CustomJournalDot: React.FC<CustomJournalDotProps> = ({
  cx,
  cy,
  payload,
  dataKey,
  color,
  isMobile,
}) => {
  if (!cx || !cy || !dataKey || !payload || payload[dataKey] === null)
    return null;

  const dotStyleKey = `${dataKey}___dotStyle`;
  const dotStyle = payload[dotStyleKey] || 'default';
  const radius = isMobile ? 2 : 3;

  if (dotStyle === 'filled') {
    // Filled circle for "Yes" responses
    return (
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill={color}
        stroke={color}
        strokeWidth={1}
      />
    );
  } else if (dotStyle === 'empty') {
    // Empty circle for "No" responses
    return (
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill="white"
        stroke={color}
        strokeWidth={1.5}
      />
    );
  } else {
    // Default style for other responses
    return (
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill="transparent"
        stroke={color}
        strokeWidth={1}
      />
    );
  }
};

interface ChartWithTimelinesProps extends ChartTimelineProps {
  chartMargins: ChartMargins;
  getDatePosition: (date: string) => number;
  allDates: string[];
  xAxisDataKey?: string;
  xAxisFormat?: string;
  yAxisOrientation?: 'left' | 'right';
  lineWeight?: number;
  onLabelHover?: (metricId: string | null) => void;
  onLabelClick?: (metricId: string) => void;
  metricsWithNumericData?: Set<string>;
  hoveredMetric?: string | null;
  lockedMetrics?: Set<string>;
  showLeftYAxis?: boolean;
  hiddenMetrics?: Set<string>;
  onLegendToggle?: (metricId: string) => void;
}

// Simplified CustomLegend
const CustomLegend = (props: any) => {
  const {
    payload = [],
    metrics,
    hiddenMetrics,
    onLegendToggle,
  }: {
    payload: any[];
    metrics: any[];
    hiddenMetrics?: Set<string>;
    onLegendToggle?: (metricId: string) => void;
  } = props;
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Filter payload to only include entries that have corresponding metrics
  const filteredPayload = payload.filter((entry: any) =>
    metrics.some((metric: any) => metric.id === entry.dataKey),
  );

  // Group entries by questionnaire (id + name)
  const groups: Record<string, any[]> = {};
  filteredPayload.forEach((entry: any) => {
    // Extract questionnaire id and name from dataKey: '{10-WEISS-P}-Total' => '10-WEISS-P'
    const match = entry.dataKey.match(/\{(.*?)\}/);
    const questionnaireKey = match ? match[1] : entry.dataKey;
    if (!groups[questionnaireKey]) groups[questionnaireKey] = [];
    groups[questionnaireKey].push(entry);
  });

  // Build legend items
  const legendItems: any[] = [];
  Object.entries(groups).forEach(([questionnaireKey, entries]) => {
    // Check if a 'Total' entry exists
    const totalEntry = entries.find((e: any) => /-Total$/.test(e.dataKey));
    if (totalEntry) {
      // Find metric for display name
      const name = questionnaireKey.substring(
        questionnaireKey.indexOf('-') + 1,
      );
      legendItems.push({
        ...totalEntry,
        abbreviation: name,
      });
    } else {
      // Show all subcategories as usual
      entries.forEach((entry: any) => {
        // Find metric for display name
        const metric = metrics.find((m: any) => m.id === entry.dataKey);
        const abbreviation = metric?.types
          ? metric.types[0]?.abbreviation
          : metric?.display || entry.value;
        legendItems.push({
          ...entry,
          abbreviation,
        });
      });
    }
  });

  return (
    <Box
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: isMobile ? '5px' : '10px',
        padding: isMobile ? '5px 0' : '10px 0',
        fontSize: isMobile ? '12px' : 'inherit',
      }}
    >
      {legendItems.map((entry: any, idx: number) => {
        const metric = metrics.find((m: any) => m.id === entry.dataKey);
        const isToggleable = metric?.isQuestionnaire === true && !!onLegendToggle;
        const isHidden = isToggleable && (hiddenMetrics?.has(entry.dataKey) ?? false);

        const chip = (
          <Box
            key={entry.dataKey || idx}
            role={isToggleable ? 'button' : undefined}
            aria-pressed={isToggleable ? !isHidden : undefined}
            onClick={
              isToggleable ? () => onLegendToggle!(entry.dataKey) : undefined
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              padding: isMobile ? '2px 6px' : '3px 8px',
              borderRadius: '4px',
              transition: 'background-color 0.2s',
              cursor: isToggleable ? 'pointer' : 'default',
              opacity: isHidden ? 0.4 : 1,
            }}
          >
            <Box
              style={{
                width: isMobile ? '10px' : '12px',
                height: isMobile ? '10px' : '12px',
                backgroundColor: entry.color,
                borderRadius: '50%',
              }}
            />
            <Text size={isMobile ? 'xs' : 'sm'} fw={500}>
              {entry.abbreviation}
            </Text>
            {isToggleable && (
              <span aria-hidden="true" style={{ opacity: 0.7 }}>
                📈
              </span>
            )}
          </Box>
        );

        if (!isToggleable) return chip;

        return (
          <MantineTooltip
            key={entry.dataKey || idx}
            label={
              isHidden ? t('graph.clickToShowSeries') : t('graph.clickToHideSeries')
            }
            openDelay={300}
            withArrow
            position="top"
          >
            {chip}
          </MantineTooltip>
        );
      })}
    </Box>
  );
};

// Custom tooltip for showing all category scores if hoverData is present

export const ChartWithTimelines: React.FC<ChartWithTimelinesProps> = ({
  data,
  metrics,
  selectedMetrics,
  chartMargins,
  getDatePosition,
  allDates,
  xAxisDataKey = 'ShortDate',
  xAxisFormat = 'day',
  yAxisOrientation = 'left',
  lineWeight = 2,
  onLabelHover,
  onLabelClick,
  metricsWithNumericData,
  hoveredMetric,
  lockedMetrics,
  showLeftYAxis = false,
  hiddenMetrics = new Set<string>(),
  onLegendToggle,
}) => {
  useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const layout = useChartLayout(chartMargins);

  // Calculate timeline offset to match Recharts plot area
  const rightYAxisWidth = yAxisOrientation === 'right' ? layout.yAxisWidth : 0;
  const timelineLeftOffset = layout.contentLeftOffset;
  const timelineWidth = `calc(100% - ${timelineLeftOffset + layout.margins.right + rightYAxisWidth}px)`;

  // Separate journal and lab metrics
  const journalMetrics = metrics.filter(
    (metric) => metric.isJournal && selectedMetrics.includes(metric.id),
  );
  const labMetrics = metrics.filter(
    (metric) => !metric.isJournal && selectedMetrics.includes(metric.id),
  );

  // Calculate min and max Y values for lab metrics only (journal metrics use discrete positions)
  const yValues = data.flatMap((d) =>
    labMetrics
      .map((metric) => {
        const v = d[metric.id];
        return typeof v === 'number' && !isNaN(v) ? v : null;
      })
      .filter((v) => v !== null),
  );

  // For journal metrics, use discrete Y positions based on their index
  const journalYPositions = journalMetrics.map((_, index) => index + 1);

  let minY, maxY, yAxisMin, yAxisMax;

  if (labMetrics.length > 0 && journalMetrics.length > 0) {
    // Both lab and journal metrics present
    const labMinY = yValues.length ? Math.min(...yValues) : 0;
    const labMaxY = yValues.length ? Math.max(...yValues) : 1;
    const journalMaxY = Math.max(...journalYPositions, 0);

    minY = Math.min(labMinY, 0);
    maxY = Math.max(labMaxY, journalMaxY);

    const yPadding = Math.max(1, (maxY - minY) * 0.1);
    yAxisMin = Math.floor(minY - yPadding);
    yAxisMax = Math.ceil(maxY + yPadding);
  } else if (labMetrics.length > 0) {
    // Only lab metrics
    minY = yValues.length ? Math.min(...yValues) : 0;
    maxY = yValues.length ? Math.max(...yValues) : 100;
    const yPadding = 1;
    yAxisMin = Math.floor(minY - yPadding);
    yAxisMax = Math.ceil(maxY + yPadding);
  } else {
    // Only journal metrics
    minY = 0;
    maxY = Math.max(...journalYPositions, 1);
    yAxisMin = 0;
    yAxisMax = maxY + 1;
  }

  // Determine the appropriate interval for X-axis ticks based on format and screen size
  const getXAxisInterval = () => {
    if (isMobile) {
      // For mobile, show fewer ticks
      switch (xAxisFormat) {
        case 'month':
          return Math.max(1, Math.floor(data.length / 3));
        case 'week':
        case 'day':
        default:
          return Math.max(1, Math.floor(data.length / 2));
      }
    } else {
      // Desktop behavior
      switch (xAxisFormat) {
        case 'month':
          return data.length > 12 ? Math.floor(data.length / 8) : 1;
        case 'week':
          return data.length > 8 ? Math.floor(data.length / 6) : 1;
        case 'day':
        default:
          return data.length > 20 ? Math.floor(data.length / 10) : 1;
      }
    }
  };

  // Calculate dynamic chart height based on number of journal questions
  const questionSpacing = isMobile ? 30 : 35; // Pixels per question
  const minChartHeight = isMobile ? 250 : 320; // Minimum height
  const baseHeight = layout.margins.top + layout.margins.bottom; // Margins

  // Note: Chart height will be calculated after filtering visible metrics

  // Calculate the Y position for each journal metric label (using visible metrics only)
  const getJournalLabelPosition = (
    index: number,
    totalVisibleQuestions: number,
  ) => {
    if (totalVisibleQuestions === 0) return 0;

    // Use equal spacing based on the question spacing
    const startOffset = layout.margins.top + 20; // Start with some padding from top
    const endOffset = chartHeight - layout.margins.bottom - 50 - 20; // End with padding from bottom
    const availableSpace = endOffset - startOffset;

    // Distribute questions evenly across available space
    const spacing =
      totalVisibleQuestions > 1
        ? availableSpace / (totalVisibleQuestions - 1)
        : 0;
    const pixelPosition = startOffset + index * spacing;

    return pixelPosition;
  };

  // First pass: create initial transformed data
  const initialTransformedData = data.map((dataPoint) => {
    const newDataPoint = { ...dataPoint };

    // For each journal metric, calculate Y position to match label position
    journalMetrics.forEach((metric) => {
      if (dataPoint[metric.id] === 1) {
        // Just mark presence for now - we'll calculate positions after filtering
        newDataPoint[metric.id] = 1;
      } else newDataPoint[metric.id] = null;
    });

    return newDataPoint;
  });

  // Filter journal metrics to only show those with data in the selected date range
  const visibleJournalMetrics = journalMetrics.filter((metric) => {
    return initialTransformedData.some(
      (dataPoint) => dataPoint[metric.id] !== null,
    );
  });

  // Recalculate chart height based on visible metrics only
  const visibleRequiredHeight =
    visibleJournalMetrics.length > 0
      ? visibleJournalMetrics.length * questionSpacing + 40 // Extra padding
      : 0;

  const chartHeight = Math.max(
    minChartHeight,
    visibleRequiredHeight + baseHeight,
  );

  // Second pass: calculate proper positions for visible metrics only
  const transformedData = initialTransformedData.map((dataPoint) => {
    const newDataPoint = { ...dataPoint };

    visibleJournalMetrics.forEach((metric, visibleIndex) => {
      if (dataPoint[metric.id] === 1) {
        // Calculate the Y value that corresponds to the label position
        const labelPixelPosition = getJournalLabelPosition(
          visibleIndex,
          visibleJournalMetrics.length,
        );

        // Convert pixel position back to data coordinates
        const startOffset = layout.margins.top;
        const endOffset = chartHeight - layout.margins.bottom - 50;
        const availableSpace = endOffset - startOffset;

        // Calculate relative position (0 to 1) where this label appears
        const relativePosition =
          visibleJournalMetrics.length > 1
            ? (labelPixelPosition - startOffset) / availableSpace
            : 0.5;

        // Map to chart data range
        const chartDataValue =
          yAxisMin + (1 - relativePosition) * (yAxisMax - yAxisMin);

        newDataPoint[metric.id] = chartDataValue;
      }
    });

    return newDataPoint;
  });

  // Custom tooltip component that has access to metrics
  const TooltipWithMetrics = ({
    active,
    payload,
    label,
  }: TooltipContentProps<ValueType, NameType>): React.ReactNode => {
    if (!active || !payload || payload.length === 0) return null;

    // Separate journal and non-journal metrics
    const journalPayload = payload.filter((entry) => {
      const dataKey = String(entry.dataKey || '');
      return dataKey.startsWith('journal-');
    });

    const nonJournalPayload = payload.filter((entry) => {
      const dataKey = String(entry.dataKey || '');
      return !dataKey.startsWith('journal-');
    });

    // Get journal values from the data point
    const journalValues: Array<{
      key: string;
      value: number | null;
      questionText: string;
      color: string;
      hasNumericValue: boolean;
    }> = [];
    if (journalPayload.length > 0 && payload[0]?.payload) {
      journalPayload.forEach((entry) => {
        const dataKey = String(entry.dataKey || '');
        const valueKey = `${dataKey}___value`;
        const questionKey = `${dataKey}___question`;

        const value = payload[0].payload[valueKey];
        const questionText = payload[0].payload[questionKey];

        // Show all journal entries that have a question text, regardless of whether they have numeric values
        if (questionText) {
          // Get the actual color from the metrics array
          const metric = metrics.find((m) => m.id === dataKey);
          const actualColor = metric?.color || '#e41a1c'; // Default to first color if not found

          journalValues.push({
            key: dataKey,
            value: value,
            questionText: questionText,
            color: actualColor,
            hasNumericValue:
              value !== null &&
              value !== undefined &&
              typeof value === 'number',
          });
        }
      });
    }

    // Don't show tooltip if no data to display
    if (nonJournalPayload.length === 0 && journalValues.length === 0)
      return null;

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '8px',
          background: 'white',
          border: '1px solid #eee',
          borderRadius: 6,
          minWidth: 160,
        }}
      >
        <span style={{ fontWeight: 700, marginBottom: 4 }}>
          {typeof label === 'string'
            ? formatChartDate(label, i18next.language, 'day')
            : String(label)}
        </span>
        {/* Display journal entries */}
        {journalValues.length > 0 &&
          journalValues.map((journal, idx) => {
            //console.log(journal);
            return (
              <div
                key={`journal-${idx}`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '12px',
                  minWidth: '100px',
                  fontWeight: 600,
                  color: journal.color,
                }}
              >
                <span>{journal.questionText}:</span>
                <span>
                  {
                    journal.hasNumericValue
                      ? journal.value!.toFixed(1)
                      : journal.value // Show checkmark for non-numeric responses
                  }
                </span>
              </div>
            );
          })}
        {/* Display non-journal metrics */}
        {nonJournalPayload.length > 0 &&
          nonJournalPayload.map((entry, idx) => {
            const entryPayload = entry.payload;
            const hoverData =
              entryPayload &&
              (entryPayload.hoverData || entryPayload.___hoverData)
                ? entryPayload.hoverData || entryPayload.___hoverData
                : null;
            return (
              <div key={`non-journal-${entry.dataKey || idx}`}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '12px',
                    minWidth: '100px',
                    fontWeight: 600,
                    color: entry.color,
                  }}
                >
                  <span>
                    {typeof entry.name === 'string' && entry.name === 'Total'
                      ? typeof entry.dataKey === 'string'
                        ? entry.dataKey.match(/\{\d+-(.*?)\}-/)?.[1] ||
                          entry.dataKey
                        : String(entry.dataKey)
                      : String(entry.name)}
                    :
                  </span>
                  <span>
                    {entry.value !== null && typeof entry.value === 'number'
                      ? entry.value.toFixed(1)
                      : String(entry.value)}
                  </span>
                </div>
                {hoverData &&
                  Object.entries(hoverData)
                    .filter(([cat]) => {
                      // Only show hoverData keys that start with the entry's dataKey
                      // entry.dataKey is like "{10-WEISSS-P}", cat is like "{10-WEISSS-P}-Total"
                      const dataKey = `{${entry.dataKey?.toString().match(/\{(.*?)\}/)?.[1]}}`;
                      return (
                        typeof dataKey === 'string' && cat.startsWith(dataKey)
                      );
                      //return typeof entry.dataKey === 'string' && cat.startsWith(entry.dataKey);
                    })
                    .sort((a, b) => {
                      const aKey = a[0].match(/\{.*?\}-(.*)/)?.[1] || '';
                      const bKey = b[0].match(/\{.*?\}-(.*)/)?.[1] || '';
                      return aKey.localeCompare(bKey);
                    })
                    .map(([cat, val]) => (
                      <div
                        key={cat}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '12px',
                          minWidth: '100px',
                          fontSize: '0.95em',
                          color: entry.color,
                        }}
                      >
                        <span>{cat.match(/\{.*?\}-(.*)/)?.[1]}:</span>
                        <span>
                          {val !== null && typeof val === 'number'
                            ? val.toFixed(1)
                            : String(val)}
                        </span>
                      </div>
                    ))}
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <Stack
      gap={isMobile ? 'xs' : 'md'}
      style={{ position: 'relative', width: '100%' }}
    >
      <Box style={{ display: 'flex', gap: 'md', width: '100%' }}>
        {/* Questions list for journal metrics */}
        {visibleJournalMetrics.length > 0 && (
          <Box
            style={{
              width: isMobile ? '150px' : '200px',
              flexShrink: 0,
              position: 'relative',
              height: `${chartHeight}px`,
            }}
          >
            {/* Positioned labels to align with chart dots - only for visible metrics */}
            {visibleJournalMetrics.map((metric, index) => {
              const topPosition = getJournalLabelPosition(
                index,
                visibleJournalMetrics.length,
              );
              const hasNumericData = metricsWithNumericData?.has(metric.id);
              const isHovered = hoveredMetric === metric.id;
              const isLocked = lockedMetrics?.has(metric.id) ?? false;

              return (
                <JournalQuestionItem
                  key={metric.id}
                  metric={metric}
                  topPosition={topPosition}
                  hasNumericData={hasNumericData || false}
                  isHovered={isHovered}
                  isLocked={isLocked || false}
                  isMobile={isMobile || false}
                  onLabelHover={onLabelHover}
                  onLabelClick={onLabelClick}
                />
              );
            })}
          </Box>
        )}

        {/* Chart */}
        <Box
          style={{
            flex: 1,
            minWidth: 0, // Allow the box to shrink
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Chart container with fixed height */}
          <Box h={chartHeight}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={transformedData} margin={layout.margins} accessibilityLayer={false}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey={xAxisDataKey}
                  tick={({ x, y, payload }) => {
                    let displayValue = payload.value;

                    // Format date using shared helper (handles both YYYY-MM-DD and YYYY-MM-DDTHH:MM)
                    if (typeof payload.value === 'string') {
                      try {
                        const currentLang = i18next.language;
                        displayValue = formatChartDate(
                          payload.value,
                          currentLang,
                          xAxisFormat,
                        );
                      } catch {
                        displayValue = payload.value;
                      }
                    }

                    return (
                      <g transform={`translate(${x},${y})`}>
                        <text
                          x={0}
                          y={0}
                          dy={10}
                          textAnchor="end"
                          fill="#666"
                          fontSize={isMobile ? 10 : 12}
                          transform="rotate(-45)"
                        >
                          {typeof displayValue === 'string' &&
                          displayValue.length > 5 &&
                          isMobile
                            ? displayValue
                                .split(' ')
                                .map((part: string, i: number) =>
                                  i === 0 ? part.charAt(0) : part,
                                )
                                .join('')
                            : displayValue}
                        </text>
                      </g>
                    );
                  }}
                  height={50}
                  interval={getXAxisInterval()}
                  padding={{ left: 0, right: 0 }}
                  allowDataOverflow={false}
                  allowDuplicatedCategory={false}
                />
                <YAxis
                  yAxisId="right"
                  orientation={yAxisOrientation}
                  width={layout.yAxisWidth}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  domain={[yAxisMin, yAxisMax]}
                  tickFormatter={(value) => {
                    // For journal-only view, hide Y-axis labels
                    if (labMetrics.length === 0) return '';
                    return value.toString();
                  }}
                />
                {showLeftYAxis && (
                  <YAxis
                    yAxisId="left"
                    orientation="left"
                    width={layout.yAxisWidth}
                    tick={{ fontSize: isMobile ? 10 : 12, fill: '#666' }}
                    domain={[0, 10]}
                    tickFormatter={(value) => value.toString()}
                  />
                )}
                <Tooltip
                  content={TooltipWithMetrics}
                  isAnimationActive={false}
                />
                {/* Render lab metrics as lines; a hidden metric keeps its dots but loses its line */}
                {labMetrics
                  .filter((metric) => !metric.isNumericJournal)
                  .map((metric) => {
                    const lineColor = metric.color;
                    const isHidden = hiddenMetrics.has(metric.id);
                    return (
                      <Line
                        key={metric.id}
                        yAxisId="right"
                        type="monotone"
                        dataKey={metric.id}
                        name={metric.display}
                        stroke={isHidden ? 'transparent' : lineColor}
                        activeDot={{
                          r: isMobile ? 6 : 8,
                          fill: lineColor,
                          stroke: lineColor,
                        }}
                        connectNulls={true}
                        dot={{
                          r: isMobile ? 3 : 4,
                          fill: lineColor,
                          stroke: lineColor,
                        }}
                        strokeWidth={
                          isHidden ? 0 : isMobile ? lineWeight * 0.75 : lineWeight
                        }
                        isAnimationActive={false}
                      />
                    );
                  })}

                {/* Render numeric journal metrics as lines when hovered or locked */}
                {metrics
                  .filter((metric) => {
                    if (
                      !metric.isNumericJournal ||
                      !selectedMetrics.includes(metric.id)
                    )
                      return false;

                    // Extract base metric ID by removing '_numeric' suffix
                    const baseMetricId = metric.id.replace('_numeric', '');
                    return (
                      hoveredMetric === baseMetricId ||
                      lockedMetrics?.has(baseMetricId)
                    );
                  })
                  .map((metric) => {
                    const lineColor = metric.color;
                    return (
                      <Line
                        key={metric.id}
                        yAxisId="left"
                        type="monotone"
                        dataKey={metric.id}
                        name={metric.display}
                        stroke={lineColor}
                        activeDot={{
                          r: isMobile ? 6 : 8,
                          fill: lineColor,
                          stroke: lineColor,
                        }}
                        connectNulls={true}
                        dot={{
                          r: isMobile ? 3 : 4,
                          fill: lineColor,
                          stroke: lineColor,
                        }}
                        strokeWidth={isMobile ? lineWeight * 0.75 : lineWeight}
                        isAnimationActive={false}
                      />
                    );
                  })}

                {/* Render journal metrics as dots only - only for visible metrics */}
                {visibleJournalMetrics.map((metric) => (
                  <Line
                    key={metric.id}
                    yAxisId="right"
                    type="monotone"
                    dataKey={metric.id}
                    name={metric.display}
                    stroke="transparent" // No line
                    strokeWidth={0}
                    dot={(props: any) => (
                      <CustomJournalDot
                        {...props}
                        color={metric.color}
                        isMobile={isMobile}
                      />
                    )}
                    activeDot={false} // Disable hover interactions completely
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* Legend outside the fixed chart area */}
          {metrics.filter(
            (metric) =>
              !metric.isJournal && selectedMetrics.includes(metric.id),
          ).length > 0 && (
            <Box mt="xs">
              <CustomLegend
                payload={metrics
                  .filter(
                    (metric) =>
                      !metric.isJournal && selectedMetrics.includes(metric.id),
                  )
                  .map((metric) => ({
                    dataKey: metric.id,
                    value: metric.display,
                    color: metric.color,
                  }))}
                metrics={metrics.filter((metric) => !metric.isJournal)}
                hiddenMetrics={hiddenMetrics}
                onLegendToggle={onLegendToggle}
              />
            </Box>
          )}

          <CombinedTimeline
            allDates={allDates}
            chartMargins={layout.margins}
            getDatePosition={getDatePosition}
            contentLeftOffset={timelineLeftOffset}
            contentWidth={timelineWidth}
          />
        </Box>
      </Box>
    </Stack>
  );
};
